import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import StoreDashboard from "@/pages/StoreDashboard";
import StorePage from "@/pages/StorePage";
import StoresIndex from "@/pages/StoresIndex";
import AdminPanel from "@/pages/AdminPanel";
import DealPage from "@/pages/DealPage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import AuthCallback from "@/pages/AuthCallback";
import ScanPage from "@/pages/ScanPage";
import QrPoster from "@/pages/QrPoster";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Nav from "@/components/app/Nav";
import Footer from "@/components/app/Footer";

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role && !(role === "store" && user.role === "admin")) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function InnerRoutes() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/stores" element={<StoresIndex />} />
      <Route path="/store/:slug" element={<StorePage />} />
      <Route path="/deal/:id" element={<DealPage />} />
      <Route path="/dashboard" element={<Protected role="store"><StoreDashboard /></Protected>} />
      <Route path="/admin" element={<Protected role="admin"><AdminPanel /></Protected>} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/scan" element={<Protected role="store"><ScanPage /></Protected>} />
      <Route path="/qr" element={<QrPoster />}/>
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppShell({ children }) {
  const isEmbed = typeof window !== "undefined" && (
    new URLSearchParams(window.location.search).get("embed") === "1" ||
    localStorage.getItem("bb_embed") === "1"
  );
  if (isEmbed && typeof window !== "undefined") {
    localStorage.setItem("bb_embed", "1");
    document.documentElement.classList.add("bb-embed-html");
    document.body.classList.add("bb-embed-html");
    // Announce content height to Joomla wrapper so it can auto-resize the iframe (single-scrollbar UX)
    const postHeight = () => {
      try { window.parent?.postMessage({ type: "bb-height", height: document.documentElement.scrollHeight }, "*"); } catch (e) { /* ignore */ }
    };
    setTimeout(postHeight, 200);
    setTimeout(postHeight, 1000);
    window.addEventListener("resize", postHeight);
    if (typeof ResizeObserver !== "undefined") {
      try { new ResizeObserver(postHeight).observe(document.body); } catch (e) { /* ignore */ }
    }
  }
  return (
    <div className={`${isEmbed ? "" : "min-h-screen"} bg-[#F8FAF8] text-slate-900 flex flex-col ${isEmbed ? "bb-embed" : ""}`}>
      {!isEmbed && <Nav />}
      <Toaster position="top-right" richColors />
      <main className="flex-1">{children}</main>
      {!isEmbed && <Footer />}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppShell>
          <InnerRoutes />
        </AppShell>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
